import type { SegmentDTO } from '@next-space/fe-api-idl';
import { BlockType, TextType } from '@next-space/fe-api-idl';
import { segmentsToText } from 'src/editor/utils/editor';
import {
  getPropertySegmentsForCopy,
  getTablePropertyValueForCopy,
} from 'src/hooks/collection-view/get-property-value';
import { getCurrentSpaceId } from 'src/hooks/space/get-space';
import type { UIState } from 'src/redux/reducers/ui';
import { cache } from 'src/redux/store';
import type { NextBlock, SelectBlock } from 'src/redux/types';
import { $currentUserCache } from 'src/services/user/current-user';
import { isLinkPageBlock, isPageLike } from 'src/utils/block-type-utils';
import { isSyncBlock } from 'src/utils/block-utils';
import { getLocationOrigin } from 'src/utils/location-utils';
import { parseSelectedCells } from 'src/utils/parse-selected-cells';
import type { BlockNode, CellContent, RecordCells } from '../types';

const getParentWithType = (uuid: string, type: BlockType) => {
  const { blocks } = cache;
  const block = blocks[uuid];
  if (block) {
    const parent = blocks[block.parentId];
    if (parent && parent.type === type) {
      return parent;
    }
  }
  return null;
};

const getColumnBlocks = (selectedBlocks: SelectBlock[]) => {
  const columnMap = new Map<NextBlock, string[]>();
  const columnListSet = new Set<NextBlock>();
  const newOrder = new Set<SelectBlock | 'column'>();

  selectedBlocks.forEach((b) => {
    const parent = getParentWithType(b.blockId, BlockType.COLUMN);
    if (parent) {
      const list = columnMap.get(parent);
      columnMap.set(parent, (list ?? []).concat(b.blockId));
      newOrder.add('column');
      return;
    }
    newOrder.add(b);
  });

  if (columnMap.size > 1) {
    [...columnMap.keys()].forEach((b) => {
      const parent = getParentWithType(b.uuid, BlockType.COLUMN_LIST);
      if (parent) {
        columnListSet.add(parent);
      }
    });
  }

  if (columnListSet.size === 1) {
    const [columnList] = [...columnListSet];
    if (columnList) {
      return [...newOrder].map((o) => {
        if (o === 'column') {
          return {
            id: columnList.uuid,
            subTree: {
              [columnList.uuid]: {
                ...columnList,
                subNodes: columnList.subNodes.filter((uuid) =>
                  [...columnMap.keys()].map((b) => b.uuid).includes(uuid)
                ),
              },
              ...[...columnMap.entries()].reduce((o, [b, subNodes]) => {
                o[b.uuid] = { ...b, subNodes };
                return o;
              }, {} as Record<string, NextBlock>),
            },
          };
        }
        return {
          id: o.blockId,
          viewId: o.viewId,
          subTree: {},
        };
      });
    }
  }

  return selectedBlocks.map((item) => ({
    id: item.blockId,
    viewId: item.viewId,
    subTree: {},
  }));
};

export const covertBlocksToNextSpaceInfo = (selectedBlocks: SelectBlock[]) => {
  const { blocks } = cache;

  const loop = (node: BlockNode, subs: string[]) => {
    subs.forEach((id) => {
      let block = blocks[id];
      if (block && (isLinkPageBlock(block) || block.type === BlockType.SYNC_REFERENCE)) {
        node.subTree[id] = block;
        block = blocks[block.data.ref?.uuid ?? ''];
      }
      if (block) {
        node.subTree[block.uuid] = block;

        const collectionLinkPage = (segments?: SegmentDTO[]) => {
          if (!segments) return;
          segments.forEach((segment) => {
            if (segment.type === TextType.LINK_PAGE) {
              const inlinePage = blocks[segment.uuid ?? ''];
              if (inlinePage) {
                node.subTree[inlinePage.uuid] = inlinePage;
              }
            }
          });
        };

        collectionLinkPage(block.data.segments);
        collectionLinkPage(block.data.caption);

        const isPage =
          block.type === BlockType.PAGE ||
          block.type === BlockType.COLLECTION_VIEW_PAGE ||
          block.type === BlockType.FOLDER;

        if (isPage) return;

        const { subNodes } = block;
        if (subNodes.length) {
          loop(node, subNodes);
        }
      }
    });
  };

  const newBlocks = getColumnBlocks(selectedBlocks);
  newBlocks.forEach((o) => {
    if (Object.keys(o.subTree).length) {
      loop(
        o,
        selectedBlocks
          .filter((b) => !newBlocks.some((o) => o.id === b.blockId))
          .map((b) => b.blockId)
      );
    } else {
      loop(o, [o.id]);
    }
  });

  return {
    blocks: newBlocks,
    userId: $currentUserCache.uuid,
    spaceId: getCurrentSpaceId(),
    hideSyncMenu: selectedBlocks.some((s) => s.syncId || isSyncBlock(blocks[s.blockId]?.type)), // 同步块不能嵌套，因此需要知道复制到剪切板的有没有同步块,用于粘贴判断
  };
};

export const covertCellsToNextSpaceInfo = (selectedCells: UIState['selectedCells']) => {
  const cellsInfo = parseSelectedCells(selectedCells);
  if (!cellsInfo) return;
  const {
    topRecordIndex,
    bottomRecordIndex,
    leftPropertyIndex,
    rightPropertyIndex,
    sortedRecordIds,
    propertyIds,
  } = cellsInfo;
  // 对单元格剪切/复制其实是对单元格对应的记录进行了剪切/复制
  // 从左上到右下方向分别把单元格的内容组合成一个大字符串并塞到剪切板上
  const selectCellValue: string[] = [];
  const records: RecordCells[] = [];
  for (let recordIndex = topRecordIndex; recordIndex <= bottomRecordIndex; recordIndex++) {
    const recordText: string[] = [];
    const recordCells: RecordCells = { cells: [] };
    for (
      let propertyIndex = leftPropertyIndex;
      propertyIndex <= rightPropertyIndex;
      propertyIndex++
    ) {
      const recordId = sortedRecordIds[recordIndex];
      const propertyId = propertyIds[propertyIndex];
      if (recordId && propertyId) {
        let propertyValue = getTablePropertyValueForCopy(recordId, propertyId, recordIndex + 1);
        let propertySegments = getPropertySegmentsForCopy(recordId, propertyId, recordIndex + 1);
        if (!propertyValue) {
          propertyValue = '';
        }
        if (!propertySegments) {
          propertySegments = [];
        }
        if (propertyValue instanceof Array) {
          propertyValue = propertyValue.join(',');
        }
        recordText.push(propertyValue.toString());
        recordCells.cells.push(propertySegments);
      }
    }
    records.push(recordCells);
    selectCellValue.push(recordText.join('\t'));
  }
  const allText = selectCellValue.join('\n');
  return { records, plainText: allText };
};

// 目前没用
export const covertBlocksToHtml = (blockNode: BlockNode[]) => {
  // 简单处理一下
  let html = '';
  const loop = (subTree: BlockNode['subTree'], id: string) => {
    const block = subTree[id];
    if (!block) return;
    if (isPageLike(block.type)) {
      html += `<p><a href="${getLocationOrigin()}/${block.uuid}">${segmentsToText(
        block.data.segments
      )}</a></p>`;
    } else {
      html += `<p>${segmentsToText(block.data.segments)}</p>`;
      block.subNodes.forEach((subNodeId) => {
        loop(subTree, subNodeId);
      });
    }
  };
  blockNode.forEach((node) => {
    loop(node.subTree, node.id);
  });
  return html;
};

export const convertTableCells = (selectedCells: UIState['selectedCells']) => {
  const _records: RecordCells[] = [];

  const first = selectedCells[0];
  if (!first) return;
  const { viewId } = first;
  const table = cache.blocks[viewId];
  if (!table) return;

  const { subNodes, data } = table;
  const column = data.format?.tableBlockColumnOrder;
  if (!column) return;

  for (const cell of selectedCells) {
    const { recordId, propertyId } = cell;
    const row = cache.blocks[recordId];
    if (!row) continue;
    const segments = row.data.collectionProperties?.[propertyId] ?? [];

    const rowIndex = subNodes.indexOf(recordId);
    const columnIndex = column.indexOf(propertyId);

    const _row = _records[rowIndex];
    if (!_row) {
      const cells: CellContent[] = [];
      cells[columnIndex] = segments;
      _records[rowIndex] = { cells };
    } else {
      _row.cells[columnIndex] = segments;
    }
  }

  const records = _records.filter(Boolean).map((o) => ({ cells: o.cells.filter(Boolean) }));
  const plain = records.reduce((str, { cells }) => {
    str += `${cells.map((segments) => segmentsToText(segments)).join('\t')}\n`;
    return str;
  }, '');

  return {
    records,
    plain,
  };
};

export const convertTableToHtml = (records: RecordCells[]) => {
  return `<table><tbody>${records
    .map(
      ({ cells }) =>
        `<tr>${cells.map((segments) => `<td>${segmentsToText(segments)}</td>`).join('')}</tr>`
    )
    .join('')}</tbody></table>`;
};
